import { useCommunityContext } from 'providers';
import { useQuery } from 'react-query';

import { MarketplaceV1 } from '@jebel/constants';

import { UserKeyFilter } from 'shared/graphql';
import { useHasSession } from 'shared/hooks';

export const USER_COMMUNITY_PRODUCTS_QUERY_KEY = 'USER_COMMUNITY_PRODUCTS';

/**
 * React hook to fetch a specific user's community products for the selected community.
 *
 * Uses React Query to asynchronously retrieve community product data from the marketplace API.
 * The query is enabled only if there is an active session and a selected community.
 *
 * @param {UserKeyFilter} user - The user whose community products should be fetched.
 * @returns The query result containing the community product data.
 * @throws {Error} If the user or selected community is not available, or if the fetch fails.
 */
export function useUserCommunityProducts(user: UserKeyFilter | null | undefined) {
  const hasSession = useHasSession();

  const { selectedCommunity } = useCommunityContext();

  return useQuery({
    enabled: Boolean(hasSession && selectedCommunity),
    queryKey: [USER_COMMUNITY_PRODUCTS_QUERY_KEY, selectedCommunity?.id, user?.id],

    async queryFn() {
      if (!user?.id) {
        throw new Error('No current user');
      }

      if (!selectedCommunity?.id) {
        throw new Error('No selected community');
      }

      const url = new URL(
        `/community/${selectedCommunity.id}/user/${user.id}/product`,
        process.env.REACT_APP_MARKETPLACE_API_URL,
      );

      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`Failed to fetch user community products (HTTP ${response.status})`);
      }

      const data = await response.json();
      return data as MarketplaceV1.UserCommunityProduct[];
    },
  });
}
